import { type FC, useMemo, useState, memo, useEffect, useRef } from 'react';
import { Box, keyframes, SxProps } from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from 'src/redux/rootReducerType';
import type { AlertContext } from 'src/redux/slices/Compliance/CertificationAlertsSlice';
import CertificationAlert from './CertificationAlert';

interface CertificationAlertsHeaderProps {
  context: AlertContext;
  isUserActive: boolean;
  sx?: SxProps;
}

const MAX_VISIBLE_ALERTS = 3;

const slideOut = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-20px);
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CertificationAlertsHeader: FC<CertificationAlertsHeaderProps> = ({
  context,
  isUserActive,
  sx = {},
}) => {
  const alerts = useSelector(
    (state: RootState) => state.certificationAlerts?.[context] ?? [],
    shallowEqual
  );

  const [isPaused, setIsPaused] = useState(false);
  const prevVisibleAlertIdsRef = useRef<Set<string>>(new Set());

  // Get active alerts, sort by createdAt descending (newest first), limit to MAX_VISIBLE_ALERTS
  const visibleAlerts = useMemo(() => {
    const active = alerts.filter((alert) => !alert.completed);
    return active
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, MAX_VISIBLE_ALERTS);
  }, [alerts]);

  // Track which alerts are leaving/entering for animations
  const [leavingAlertIds, setLeavingAlertIds] = useState<Set<string>>(
    new Set()
  );
  const [enteringAlertIds, setEnteringAlertIds] = useState<Set<string>>(
    new Set()
  );

  // Keep leaving alerts in render list temporarily for animation
  const alertsToRender = useMemo(() => {
    const currentIds = new Set(visibleAlerts.map((a) => a.id));
    const allRelevantIds = new Set([...currentIds, ...leavingAlertIds]);

    // Get all alerts that should be rendered (current + leaving)
    return alerts
      .filter((alert) => allRelevantIds.has(alert.id) && !alert.completed)
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [alerts, visibleAlerts, leavingAlertIds]);

  useEffect(() => {
    const currentIds = new Set(visibleAlerts.map((a) => a.id));
    const prevIds = prevVisibleAlertIdsRef.current;

    // Find alerts that are leaving (in prev but not current)
    const leaving = new Set(
      Array.from(prevIds).filter((id) => !currentIds.has(id))
    );
    // Find alerts that are entering (in current but not prev)
    const entering = new Set(
      Array.from(currentIds).filter((id) => !prevIds.has(id))
    );

    if (leaving.size > 0) {
      setLeavingAlertIds((prev) => new Set([...prev, ...leaving]));
      // Clear leaving state after animation
      setTimeout(() => {
        setLeavingAlertIds((prev) => {
          const updated = new Set(prev);
          leaving.forEach((id) => updated.delete(id));
          return updated;
        });
      }, 300);
    }

    if (entering.size > 0) {
      setEnteringAlertIds((prev) => new Set([...prev, ...entering]));
      // Clear entering state after animation
      setTimeout(() => {
        setEnteringAlertIds((prev) => {
          const updated = new Set(prev);
          entering.forEach((id) => updated.delete(id));
          return updated;
        });
      }, 300);
    }

    prevVisibleAlertIdsRef.current = currentIds;
  }, [visibleAlerts]);

  if (alertsToRender.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        justifySelf: 'center',
        zIndex: 2,
        p: 2,
        mb: 2,
        gap: 1,
        ...sx,
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {alertsToRender.map((alert) => {
        const isLeaving = leavingAlertIds.has(alert.id);
        const isEntering = enteringAlertIds.has(alert.id);
        const isVisible = visibleAlerts.some((a) => a.id === alert.id);

        return (
          <Box
            key={alert.id}
            sx={{
              animation: isLeaving
                ? `${slideOut} 0.3s ease-out forwards`
                : isEntering
                ? `${fadeIn} 0.3s ease-out`
                : 'none',
            }}
          >
            <CertificationAlert
              alert={alert}
              context={context}
              isUserActive={isUserActive}
              isPaused={isPaused}
              isVisible={isVisible}
              disableAutoRead={true}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default memo(CertificationAlertsHeader);
